import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Table } from "@/components/Table";
import { useGetContactHistoryTable } from "@/requests/hooks/collect";
import { formatDate } from "@/utils/helpers";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

interface IProps {
  sectionsRef: MutableRefObject<HTMLDivElement | null>;
  channel: IContactChannel;
}

const ContactHistoryTable = ({ sectionsRef, channel }: IProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<IContactHistory>();
  const { id } = useParams();
  const navigate = useNavigate();

  const navigateAndScroll =
    (contactHistoryId: string, contactDetailsId?: string) => () => {
      navigate(
        `/${channel === "EMAIL" ? "marketing" : "sms"}/audit-logs/${contactDetailsId}/history/${contactHistoryId}`,
      );

      sectionsRef.current?.scrollIntoView({ behavior: "smooth" });
    };

  const columns = [
    columnHelper.accessor("contactHistoryId", {
      header: t("common.contactDetailsView.historyTable.contactHistoryId"),
    }),
    columnHelper.accessor("marketable", {
      header: t("common.contactDetailsView.historyTable.status.title"),
      cell: info => {
        const isMarketable = info.getValue();

        return (
          <Badge variant={isMarketable ? "success" : "error"}>
            {t(
              `common.contactDetailsView.historyTable.status.${isMarketable ? "marketable" : "nonMarketable"}`,
            )}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: t("common.contactDetailsView.historyTable.rationale"),
      cell: info => t(`common.contacts.rationale.${info.getValue()}`),
    }),
    columnHelper.accessor("date", {
      header: t("common.contactDetailsView.historyTable.date"),
      cell: info => formatDate(info.getValue()),
    }),
    columnHelper.accessor("contactId", {
      header: t("common.contactDetailsView.historyTable.details"),
      cell: info => {
        const rowData = info.row.original;

        return (
          <Button
            as="button"
            onClick={navigateAndScroll(rowData.contactHistoryId, id)}
            variant="link"
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="text-secondary">
            {t("common.auditLogs.table.detailsButton")}
          </Button>
        );
      },
    }),
  ];

  return (
    <Table
      columns={columns}
      queryHook={useGetContactHistoryTable}
      queryKey={["common", "contactHistoryTable", id as string]}
      filters={{ contactId: id as string }}
      denyList={["contactId"]}
      defaultFilters={{ pageSize: 5 }}
    />
  );
};

export default ContactHistoryTable;
